import { cn } from "@/lib/utils";

/**
 * A single credit pack card. Display-only on the Pricing page; pass `onBuy`
 * to make the whole card a purchase button (used inside the Buy Credits modal).
 *
 * @param {object} props
 * @param {object} props.pack - one entry from REPORT_PACKS / AI_PACKS
 * @param {string} props.unit - singular unit label, e.g. "report" or "AI credit"
 * @param {(pack: object) => void} [props.onBuy] - when provided, card is clickable
 * @param {string|null} [props.loading] - id of the pack currently redirecting
 * @param {boolean} [props.hideUnit] - hide the unit beside the amount (the footer still names it)
 */
export function CreditPackCard({ pack, unit, onBuy, loading, hideUnit }) {
  const interactive = typeof onBuy === "function";
  const busy = loading === pack.id;

  const Comp = interactive ? "button" : "div";

  return (
    <Comp
      type={interactive ? "button" : undefined}
      onClick={interactive ? () => onBuy(pack) : undefined}
      disabled={interactive ? busy : undefined}
      className={cn(
        "relative rounded-xl border bg-card flex flex-col p-4 text-left",
        pack.best ? "border-primary" : "border-border",
        interactive &&
          "cursor-pointer transition-[border-color,transform,box-shadow] hover:border-primary hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60 disabled:pointer-events-none"
      )}
    >
      {pack.best && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[0.55rem] font-bold tracking-[0.07em] uppercase px-2.5 py-[0.18rem] rounded-full bg-primary text-primary-foreground whitespace-nowrap">
          Best value
        </span>
      )}
      <span className="text-[0.75rem] font-semibold text-foreground mb-2">{pack.label}</span>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display text-[2rem] font-normal leading-none text-foreground">{pack.amount}</span>
        {!hideUnit && <span className="text-[0.8125rem] text-muted-foreground">{unit}s</span>}
      </div>
      <div className="text-[1rem] font-medium text-foreground mt-1.5">{pack.price}</div>
      <div className="mt-auto pt-3 border-t border-border text-[0.75rem] text-muted-foreground">
        {busy ? "Redirecting…" : `${pack.perCredit} per ${unit}`}
      </div>
    </Comp>
  );
}
